import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';

type ConfirmationType = 'default' | 'destructive';

type ConfirmResult = {
    readonly dontShowAgain: boolean;
};

type ConfirmOptions = {
    readonly cancelButtonLabel?: string;
    readonly confirmButtonLabel?: string;
    readonly confirmButtonVariant?: 'default' | 'destructive' | 'secondary';
    readonly description: ReactNode;
    readonly renderCancelButton?: (onCancel: () => void) => ReactNode;
    readonly renderConfirmButton?: (onConfirm: () => void) => ReactNode;
    readonly scrollableContent?: ReactNode;
    readonly showDontShowAgainCheckbox?: boolean;
    readonly title: string;
    readonly type?: ConfirmationType;
};

type ConfirmationRequest = {
    readonly cancelButtonLabel: string;
    readonly confirmButtonLabel: string;
    readonly confirmButtonVariant: 'default' | 'destructive' | 'secondary';
    readonly description: ReactNode;
    readonly reject: (reason?: unknown) => void;
    readonly renderCancelButton?: (onCancel: () => void) => ReactNode;
    readonly renderConfirmButton?: (onConfirm: () => void) => ReactNode;
    readonly resolve: (value: ConfirmResult) => void;
    readonly scrollableContent?: ReactNode;
    readonly showDontShowAgainCheckbox: boolean;
    readonly title: string;
};

type ConfirmContextType = {
    readonly confirm: (options: ConfirmOptions) => Promise<ConfirmResult>;
};

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

type ConfirmProviderProps = PropsWithChildren;

export const ConfirmProvider: FunctionComponent<ConfirmProviderProps> = ({ children }) => {
    const { translate } = useContext(ApplicationContext);

    const buildDefaults = useCallback(
        (type: ConfirmationType) => {
            switch (type) {
                case 'destructive':
                    return {
                        cancelButtonLabel: translate('common.cancel'),
                        confirmButtonLabel: translate('common.delete'),
                        confirmButtonVariant: 'destructive' as const,
                    };
                // biome-ignore lint/complexity/noUselessSwitchCase: suppressed due to migration
                case 'default':
                default:
                    return {
                        cancelButtonLabel: translate('common.cancel'),
                        confirmButtonLabel: translate('common.confirm'),
                        confirmButtonVariant: 'default' as const,
                    };
            }
        },
        [translate],
    );

    const [confirmationRequest, setConfirmationRequest] = useState<ConfirmationRequest | null>(null);
    const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);

    const confirm = useCallback(
        (options: ConfirmOptions): Promise<ConfirmResult> => {
            return new Promise<ConfirmResult>((resolve, reject) => {
                const type = options.type ?? 'default';
                const defaults = buildDefaults(type);

                setDontShowAgainChecked(false);
                setConfirmationRequest({
                    cancelButtonLabel: options.cancelButtonLabel ?? defaults.cancelButtonLabel,
                    confirmButtonLabel: options.confirmButtonLabel ?? defaults.confirmButtonLabel,
                    confirmButtonVariant: options.confirmButtonVariant ?? defaults.confirmButtonVariant,
                    description: options.description,
                    reject,
                    renderCancelButton: options.renderCancelButton,
                    renderConfirmButton: options.renderConfirmButton,
                    resolve,
                    scrollableContent: options.scrollableContent,
                    showDontShowAgainCheckbox: options.showDontShowAgainCheckbox ?? false,
                    title: options.title,
                });
            });
        },
        [buildDefaults],
    );

    const handleConfirm = useCallback(() => {
        confirmationRequest?.resolve({ dontShowAgain: dontShowAgainChecked });
        setConfirmationRequest(null);
        setDontShowAgainChecked(false);
    }, [confirmationRequest, dontShowAgainChecked]);

    const handleCancel = useCallback(() => {
        confirmationRequest?.reject('Cancelled by user');
        setConfirmationRequest(null);
        setDontShowAgainChecked(false);
    }, [confirmationRequest]);

    const contextValue: ConfirmContextType = {
        confirm,
    };

    return (
        <ConfirmContext.Provider value={contextValue}>
            {children}
            {confirmationRequest !== null && (
                <ConfirmationDialog
                    cancelButtonLabel={confirmationRequest.cancelButtonLabel}
                    confirmButtonLabel={confirmationRequest.confirmButtonLabel}
                    confirmButtonVariant={confirmationRequest.confirmButtonVariant}
                    description={confirmationRequest.description}
                    dontShowAgain={
                        confirmationRequest.showDontShowAgainCheckbox
                            ? {
                                  checked: dontShowAgainChecked,
                                  label: translate('common.dont-show-again'),
                                  onChange: setDontShowAgainChecked,
                              }
                            : undefined
                    }
                    open={true}
                    renderCancelButton={confirmationRequest.renderCancelButton}
                    renderConfirmButton={confirmationRequest.renderConfirmButton}
                    scrollableContent={confirmationRequest.scrollableContent}
                    title={confirmationRequest.title}
                    onCancel={handleCancel}
                    onConfirm={handleConfirm}
                />
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = (): ConfirmContextType => {
    const context = useContext(ConfirmContext);
    if (context === undefined) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }

    return context;
};
